<template>
  <v-container class="modern-detail-view pb-12">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid, validated }">
      <!-- Global Header -->
      <v-row class="mb-8 align-center px-4 pt-4">
        <v-btn icon @click.stop="$router.go(-1)" class="mr-4 grey lighten-4 shadow-sm">
          <v-icon color="primary" small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white">fas fa-file-invoice</v-icon>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-black primary--text lh-1">
              {{ id > 0 ? `Actualizar Documento #${id}` : 'Registrar Documento' }}
            </div>
            <div class="mt-1 d-flex align-center">
              <v-chip :color="dte.tipo === 30 ? 'primary' : 'secondary'" dark x-small label class="font-weight-black px-2 mr-2">
                <v-icon x-small left>{{ dte.tipo === 30 ? 'fas fa-file-invoice' : 'fas fa-file-invoice-dollar' }}</v-icon>
                {{ dte.tipo === 30 ? 'Factura' : 'Nota de Crédito' }}
              </v-chip>
              <span class="caption grey--text font-weight-bold uppercase tracking-widest">Ventas y Facturación Electrónica</span>
            </div>
          </div>
        </div>
        <v-spacer></v-spacer>
        
        <v-btn 
          :color="invalid && validated ? 'error' : 'success'" 
          class="font-weight-black shadow-sm px-8 h-44" 
          @click="handleSubmit(confirm)" 
          :loading="loading"
        >
          <v-icon left small>{{ invalid && validated ? 'fas fa-exclamation-triangle' : 'fas fa-save' }}</v-icon> 
          {{ invalid && validated ? 'Corregir Errores' : (factronicaActivatedEnabled ? 'Guardar Factronica' : 'Guardar Documento') }}
        </v-btn>
      </v-row>

      <v-form @submit.prevent="handleSubmit(confirm)" @keydown.enter.prevent>
        <v-row>
          <!-- Configuration Sidebar -->
          <v-col cols="12" md="3">
            <!-- Basic Info Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8" :class="invalid && validated ? 'border-error-light' : ''">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Información del Documento</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">Vincular a OT</div>
              <v-text-field
                v-model="work_order"
                placeholder="Nro de Orden de Trabajo..."
                outlined dense
                class="modern-input mb-4"
                :append-icon="work_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                :loading="loadingWorkOrder"
                @keydown="searchWorkOrder"
                :success="work_order_finded"
                :hint="alertHasInvoices ? 'Ya existe un documento para esta OT' : ''"
                :persistent-hint="alertHasInvoices"
              ></v-text-field>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Documento</div>
              <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="dte.tipo"
                  :items="tipos"
                  item-text="text"
                  item-value="value"
                  outlined dense
                  :hide-details="!errors[0]"
                  class="modern-input mb-4"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <v-row dense>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Nro Folio</div>
                  <ValidationProvider name="Folio" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="dte.document_id"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Fecha Emisión</div>
                  <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="dte.date_emited"
                      type="date"
                      outlined dense
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>

            <!-- Receptor Card -->
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Datos del Receptor</div>
              
              <div class="caption grey--text font-weight-black mb-1 ml-1">{{$store.getters.getInternational.tax_id_label}}</div>
              <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.rutRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :loading="loadingCustomer"
                  :error-messages="errors"
                  @keyup="getCustomerDataByRut"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Razón Social</div>
              <ValidationProvider name="Razon Social" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.rznSocRecep"
                  outlined dense
                  class="modern-input mb-4"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Giro Comercial</div>
              <ValidationProvider name="Giro" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="dte.giroRecep"
                  outlined dense
                  class="modern-input"
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>

              <v-alert type="info" dense text v-if="alertCustomerNotInDB" class="mt-4 caption">
                Cliente no registrado en el sistema
              </v-alert>
            </v-card>

            <!-- Additional Detail Card -->
            <v-card outlined class="modern-card shadow-sm pa-6">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Detalles Adicionales</div>
              
              <v-row dense>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Días Venc.</div>
                  <v-text-field
                    v-model="dte.payment"
                    type="number"
                    outlined dense
                    class="modern-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Contacto</div>
                  <v-select
                    :items="contactList"
                    item-text="email"
                    return-object
                    outlined dense
                    placeholder="Elegir..."
                    class="modern-input"
                    @change="selectContact"
                  ></v-select>
                </v-col>
              </v-row>

              <div class="caption grey--text font-weight-black mb-1 ml-1 mt-4">Referencia General</div>
              <v-text-field
                v-model="dte.glosa"
                placeholder="Glosa del documento..."
                outlined dense
                class="modern-input"
              ></v-text-field>

              <v-divider class="my-6"></v-divider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Dirección de Despacho</div>
              <v-select
                :items="addressList"
                item-text="name"
                return-object
                outlined dense
                placeholder="Elegir dirección..."
                class="modern-input mb-4"
                @change="selectAddress"
              ></v-select>

              <v-text-field
                v-model="dte.dirRecep"
                label="Dirección"
                outlined dense
                class="modern-input mb-4"
                hide-details
              ></v-text-field>
              
              <v-text-field
                v-model="dte.cmnaRecep"
                :label="$store.getters.getInternational.comune_label"
                outlined dense
                class="modern-input"
                hide-details
              ></v-text-field>
            </v-card>
          </v-col>

          <!-- Main Content: Products table -->
          <v-col cols="12" md="9">
            <div class="mb-4 d-flex align-center">
              <v-avatar color="primary" size="32" class="mr-3 shadow-xs">
                <v-icon color="white" x-small>fas fa-boxes</v-icon>
              </v-avatar>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest">
                Detalle de Productos y Servicios
              </div>
              <v-chip v-if="sales_note" color="primary lighten-5" class="primary--text ml-4 font-weight-bold" small label>
                Cotización #{{sales_note}}
              </v-chip>
              <v-spacer></v-spacer>
              <v-btn color="success" class="font-weight-black rounded-lg px-6 shadow-sm" @click="()=>dte.dte_detail.push({
                nro_linea:dte.dte_detail.length + 1,
                product:-1,
                sku:'',
                name:'',
                unit:'',
                qty:0,
                price:0
              })">
                <v-icon left small>fas fa-plus</v-icon> Agregar Línea
              </v-btn>
            </div>

            <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8">
              <v-data-table
                :headers="headers"
                :items="dte.dte_detail"
                item-key="nro_linea"
                hide-default-footer
                class="modern-table"
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="pa-10 text-center fill-width grey--text">
                    <v-icon size="48" color="grey lighten-3" class="mb-4">fas fa-receipt</v-icon>
                    <div class="body-1 font-weight-bold uppercase mb-2">No hay productos en el documento</div>
                    <div class="caption">Agregue líneas de detalle para calcular los totales</div>
                  </div>
                </template>

                <template v-slot:item.sku="{ item }">
                  <v-text-field   
                    v-model='item.sku'
                    outlined dense hide-details
                    class="modern-input-minimal font-weight-black primary--text"
                    @keyup="getProductDatabySKU(item)"
                  >
                    <template v-slot:append v-if="item.product<0 && item.sku != ''">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="warning" v-bind="attrs" v-on="on">fas fa-exclamation-triangle</v-icon>
                        </template>
                        <span>Producto no encontrado en inventario</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </template>

                <template v-slot:item.name="{ item }">
                  <v-textarea  
                    v-model='item.name'
                    rows="1"
                    auto-grow
                    outlined dense hide-details
                    class="modern-input-minimal body-2 py-2"
                  />
                </template>

                <template v-slot:item.qty="{ item }">
                  <v-text-field
                    type="number"
                    v-model.number='item.qty'
                    :min="0"
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.unit="{ item }">
                  <v-text-field
                    v-model='item.unit'
                    outlined dense hide-details
                    class="modern-input-minimal text-center"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.price="{ item }">
                  <v-text-field
                    type="number"
                    v-model.number='item.price'
                    outlined dense hide-details
                    class="modern-input-minimal text-right"
                    style="max-width: 100%"
                  ></v-text-field>
                </template>

                <template v-slot:item.total="{ item }">
                  <span class="body-2 font-weight-black primary--text">
                    {{ (item.price * item.qty) | moneda }}
                  </span>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn icon small color="error" class="grey lighten-4" @click.stop="removeArticle(item)">
                    <v-icon x-small>fas fa-trash</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>

            <!-- Totals Card -->
            <v-card outlined class="modern-card shadow-sm pa-8 bg-lightgrey">
              <v-row align="center">
                <v-col cols="12" md="3" class="border-right px-6 text-center">
                  <div class="caption grey--text font-weight-black uppercase mb-1">Subtotal Neto</div>
                  <div class="text-h5 font-weight-black primary--text">{{ neto | moneda }}</div>
                </v-col>
                
                <v-col cols="12" md="4" class="border-right px-6 text-center">
                  <div class="d-flex align-center justify-center mb-1">
                    <span class="caption grey--text font-weight-black uppercase mr-2">{{ $store.getters.getInternational.purchase_tax_label }} (%)</span>
                    <v-text-field 
                      outlined dense hide-details
                      class='modern-input-minimal'
                      style="width: 70px"
                      type='number' 
                      v-model.number="dte.iva" 
                    />
                  </div>
                  <div class="text-h6 font-weight-bold grey--text text--darken-3">
                    {{ (neto * dte.iva * 0.01) | moneda }}
                  </div>
                </v-col>

                <v-col cols="12" md="5" class="px-8 d-flex flex-column align-end">
                  <div class="caption primary--text font-weight-black uppercase tracking-wider mb-1">Total Documento</div>
                  <div class="text-h3 font-weight-black primary--text tracking-tighter">
                    {{ (neto * (1 + dte.iva / 100)) | moneda }}
                  </div>
                  <div class="caption grey--text font-weight-bold mt-2">
                    Vencimiento Estimado: <span class="primary--text">{{ payment_date }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>

    <!-- Progress Dialog -->
    <v-dialog v-model="showProgress" persistent max-width="450">
      <v-card class="modern-card shadow-sm pa-10 text-center"> 
        <div class="text-h5 font-weight-black primary--text mb-6">Procesando Documento</div>
        <v-progress-circular 
          :rotate="-90"
          :size="120"
          :width="15"
          v-model="progress"
          color="primary"
          class="mb-6"
        >
          <span class="text-h6 font-weight-black">{{ progress | number('0.0') }}%</span>
        </v-progress-circular>
        <div class="caption grey--text font-weight-black uppercase tracking-widest">Enviando datos al servidor...</div>
      </v-card>
    </v-dialog>

    <confirm 
      v-model="showConfirm" 
      :message="dte.work_order ? `Se vinculará a la OT #${dte.work_order}. ¿Confirmar registro?` : 'Se creará el registro sin vínculo a OT. ¿Confirmar?'" 
      @ok="create"
      @cancel="showConfirm=false"
    />
  </v-container>
</template>

<script>
import { createDTE, getWorkOrderforDTE } from '@/api/salesNotes'
import { getCustomer, getCustomerByRUT } from '@/api/customers'
import { getProductbySKU } from '@/api/products'
import { createFactronicaDTE } from '@/api/factronica'
import notifications from '@/utils/notifications'
import { validaRUT } from '@/utils/rut_validator'
import moment from 'moment'

export default {
  name: 'CreateDTE',
  data() {
    return {
      loading: false,
      showConfirm: false,
      rules: {
        rut: value => validaRUT(value) || 'Rut Inválido',
      },
      progress: 0,
      showProgress: false,
      id: -1,
      work_order: '',
      sales_note: '',
      loadingWorkOrder: false,
      work_order_finded: false,
      alertCustomerNotInDB: false,
      alertProductsnotInDB: false, // Added missing ref from old code
      alertHasInvoices: false,     // Added missing logic placeholder
      sales_discount: 0,
      timer: null,
      tipos: [
        { value: 30, text: 'Factura' },
        { value: 60, text: 'Nota de Crédito' }
      ],
      headers: [
        { text: 'Código/SKU', align: 'start', sortable: false, value: 'sku', class: 'table-header', width: '110px' },
        { text: 'Descripción del Producto / Servicio', align: 'start', sortable: false, value: 'name', class: 'table-header' },
        { text: 'Unidad', align: 'center', sortable: false, value: 'unit', class: 'table-header', width: '90px' },
        { text: 'Cant.', align: 'center', sortable: false, value: 'qty', width: '120px', class: 'table-header' },
        { text: 'Precio Unit.', align: 'end', sortable: false, value: 'price', width: '150px', class: 'table-header' },
        { text: 'Total', align: 'end', sortable: false, value: 'total', width: '130px', class: 'table-header' },
        { text: '', align: 'end', sortable: false, value: 'actions', width: '50px', class: 'table-header' },
      ],
      dte: {
        document_id: '',
        tipo: 30,
        date_emited: new Date().toISOString().substr(0, 10),
        customer: '',
        rutRecep: '',
        rznSocRecep: '',
        giroRecep: '',
        contactRecep: '',
        dirRecep: '',
        cmnaRecep: '',
        glosa: '',
        iva: parseInt(this.$store.getters.getCompany.tax),
        payment: 0,
        dte_detail: []
      },
      loadingCustomer: false,
      contactList: [],
      addressList: [],
    }
  },
  methods: {
    removeArticle(item) {
      this.dte.dte_detail = this.dte.dte_detail.filter(i => i.nro_linea != item.nro_linea)
    },
    confirm() {
      if (this.dte.dte_detail.length <= 0) {
        notifications.showError('Deben existir elementos en el documento')
        return
      }
      this.showConfirm = true
    },
    async getCustomerData(customer_id) {
      this.loadingCustomer = true
      try {
        const data = await getCustomer(customer_id)
        this.alertCustomerNotInDB = false
        this.dte.rutRecep = data.rut
        this.dte.rznSocRecep = data.name
        this.dte.giroRecep = data.giro
        this.dte.payment = data.payment
        this.contactList = data.contacts
        if (data.contacts.length > 0) this.selectContact(data.contacts[0])
        this.addressList = data.addresses
        if (data.addresses.length > 0) this.selectAddress(data.addresses[0])
      } catch (e) {
        notifications.showError('Error cargando datos del cliente')
      } finally {
        this.loadingCustomer = false
      }
    },
    getCustomerDataByRut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        if (validaRUT(this.dte.rutRecep)) {
          this.loadingCustomer = true
          try {
            const data = await getCustomerByRUT(this.dte.rutRecep)
            this.alertCustomerNotInDB = false
            this.dte.rznSocRecep = data.name
            this.dte.giroRecep = data.giro
            this.dte.payment = data.payment
            this.contactList = data.contacts
            if (data.contacts.length > 0) this.selectContact(data.contacts[0])
            this.addressList = data.addresses
            if (data.addresses.length > 0) this.selectAddress(data.addresses[0])
          } catch (e) {
            notifications.showInfo('Cliente no encontrado en BBDD')
            this.alertCustomerNotInDB = true
          } finally {
            this.loadingCustomer = false
          }
        }
      }, 1000)
    },
    getProductDatabySKU(item) {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        try {
          const data = await getProductbySKU(item.sku)
          item.product = data.id
          item.name = data.name
          item.price = data.price
          item.unit = data.sell_unit_m2 ? 'MTS2' : 'UN'
          item.qty = 1
        } catch (e) {
          item.product = -1
        }
      }, 1000)
    },
    selectContact(val) {
      const { first_name, last_name } = val
      this.dte.contactRecep = `${first_name} ${last_name}`
    },
    selectAddress(val) {
      const { street, number, comune } = val
      this.dte.dirRecep = `${street} ${number}`
      this.dte.cmnaRecep = comune
    },
    searchWorkOrder() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        if (!this.work_order) return
        this.loadingWorkOrder = true
        try {
          const data = await getWorkOrderforDTE(this.work_order)
          this.work_order_finded = true
          const { customer, title, products, id, additional_costs, totals, sales_note, has_invoices } = data
          this.alertHasInvoices = has_invoices
          this.sales_note = sales_note
          this.dte.customer = customer.id
          this.dte.rutRecep = customer.rut
          this.dte.rznSocRecep = customer.name
          this.dte.giroRecep = customer.giro
          this.dte.payment = customer.payment
          this.contactList = customer.contacts
          if (customer.contacts.length > 0) this.selectContact(customer.contacts[0])
          this.addressList = customer.addresses
          if (customer.addresses.length > 0) this.selectAddress(customer.addresses[0])
          this.dte.work_order = id
          this.dte.glosa = title
          this.sales_discount = totals.discount
          this.dte.dte_detail = []
          
          products.forEach(p => {
            this.dte.dte_detail.push({
              nro_linea: this.dte.dte_detail.length + 1,
              product: p.id,
              sku: p.sku,
              name: p.name,
              unit: p.sell_unit_m2 ? 'MTS2' : 'UN',
              qty: p.sell_unit_m2 ? p.totalMts2 : p.qty,
              price: this.$options.filters.number((this.$options.filters.number(p.price, '0') * (1 - totals.discount * 0.01)), '0'),
            })
          })
          additional_costs.forEach(c => {
            this.dte.dte_detail.push({
              nro_linea: this.dte.dte_detail.length + 1,
              product: -1,
              sku: c.name.toUpperCase().slice(0, 4),
              name: c.name,
              unit: c.cost_unit,
              qty: c.unit === 1 ? totals.mts2 : c.qty,
              price: this.$options.filters.number((this.$options.filters.number(c.sell_price, '0') * (1 - totals.discount * 0.01)), '0'),
            })
          })
        } catch (e) {
          notifications.showInfo('Orden de Trabajo no encontrada')
          this.work_order_finded = false
        } finally {
          this.loadingWorkOrder = false
        }
      }, 800)
    },
    async create() {
      this.showConfirm = false
      if (this.dte.dte_detail.length <= 0) {
        notifications.showError('Deben existir items en el documento')
        return
      }
      this.loading = true
      
      const payload = JSON.parse(JSON.stringify(this.dte))
      if (!payload.customer) delete payload.customer
      
      // Ajuste de precios para NC
      if (payload.tipo == 60) {
        payload.dte_detail.forEach(d => { d.price = -1 * d.price })
      }

      try {
        if (this.factronicaActivatedEnabled) {
          await createFactronicaDTE(payload)
          notifications.showSuccess('Documento creado en Factronica')
        } else {
          await createDTE(payload)
          notifications.showSuccess('Documento registrado con éxito')
        }
        this.$router.push('/sales/invoices')
      } catch (e) {
        notifications.showError('Error al procesar documento: ' + e)
      } finally {
        this.loading = false
      }
    }
  },
  computed: {
    neto() {
      return this.dte.dte_detail.reduce((acc, val) => acc + parseFloat(val.qty || 0) * parseFloat(val.price || 0), 0)
    },
    payment_date() {
      return moment(this.dte.date_emited).add(this.dte.payment, 'days').format('DD/MM/YYYY')
    },
    factronicaActivatedEnabled() {
      return this.$store.getters.getCompany.factronica_token ? true : false
    }
  },
  mounted() {
    if (this.$route.params.id > 0) {
      this.id = this.$route.params.id
      // Aquí iría la lógica de carga si CreateDTE soportara edición (actualmente parece orientado a creación)
    }
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; transition: all 0.3s ease; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-right { border-right: 1px solid rgba(0,0,0,0.05) !important; }
.border-error-light { border: 1px solid rgba(var(--v-error-base), 0.3) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1.5px; }
.h-44 { height: 44px !important; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #cbd5e1 !important; }
.modern-input-minimal >>> input, .modern-input-minimal >>> textarea { font-size: 0.85rem !important; padding: 4px 6px !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 52px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }

.v-btn.shadow-sm { box-shadow: 0 4px 10px rgba(0,0,0,0.1) !important; }
</style>